import { atom } from 'jotai'

// Types matching the backend response
export interface AmazonListing {
  itemName: string
  asin: string
  sellerSku: string
  price: string
  quantity: string
  status: string
  openDate: string
  imageUrl: string
  // Catalog enrichment fields
  brand: string
  manufacturer: string
  modelNumber: string
  countryOfOrigin: string
  upc: string
  ean: string
  material: string
  // Sale/promotion fields (from TSV report)
  salePrice: string         // promotional sale price from report
  saleStartDate: string     // sale start date
  saleEndDate: string       // sale end date
  // Pricing enrichment fields (from Product Pricing API)
  currentPrice: string    // actual selling price (includes promotions/sales)
  regularPrice: string    // original price before any promotion
  currencyCode: string    // currency code (e.g. "USD")
  // Listing content fields (from Listings Items API)
  bulletPoints: string[]  // product bullet points
  description: string     // product description
  searchTerms: string     // backend search terms / keywords
}

const API_BASE = import.meta.env.VITE_API_BASE || ''

// ─── sessionStorage cache ────────────────────────────────────────────────────
// Guarantees data survives navigation / atom reinitialisation.

const CACHE_KEY = 'vertco_amazon_listings'

function readCache(): AmazonListing[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.listings ?? null
  } catch {
    return null
  }
}

function writeCache(listings: AmazonListing[]) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ listings }))
  } catch { /* quota exceeded — ignore */ }
}

// ─── Core atoms (writable derived — lazy sessionStorage fallback) ────────────
//
// The internal "_raw" atoms hold the in-memory value (null = never set).
// The public atoms read _raw first; if null they fall back to sessionStorage.
// This means even if the Jotai store resets an atom to its initial value,
// the getter always recovers data from the browser cache.

const _rawListingsAtom = atom<AmazonListing[] | null>(null)

/** The fetched listings data — persists via sessionStorage */
export const listingsAtom = atom(
  (get): AmazonListing[] => {
    const val = get(_rawListingsAtom)
    if (val !== null) return val
    return readCache() ?? []
  },
  (_get, set, listings: AmazonListing[]) => {
    set(_rawListingsAtom, listings)
    writeCache(listings)
  },
)

const _rawFetchedAtom = atom<boolean | null>(null)

/** Whether the initial fetch has completed at least once */
export const fetchedAtom = atom(
  (get): boolean => {
    const val = get(_rawFetchedAtom)
    if (val !== null) return val
    return readCache() !== null
  },
  (_get, set, fetched: boolean) => {
    set(_rawFetchedAtom, fetched)
  },
)

/** Current search query */
export const searchAtom = atom('')

/** Status filter — 'all' shows everything, otherwise filters by status */
export type StatusFilter = 'all' | 'active' | 'inactive' | 'incomplete'
export const statusFilterAtom = atom<StatusFilter>('all')

export const loadingAtom = atom(false)
export const errorAtom = atom<string | null>(null)

// ─── Pre-fill atom (Amazon → Label Generator) ────────────────────────────────
// Written by AmazonListings page before navigating to Label Generator.
// Read by LabelGeneratorStore.loader() to pre-fill the label.

const PREFILL_KEY = 'vertco_prefill_listing'

const _rawPrefillAtom = atom<AmazonListing | null>(null)

export const prefillListingAtom = atom(
  (get): AmazonListing | null => {
    const val = get(_rawPrefillAtom)
    if (val !== null) return val
    try {
      const raw = sessionStorage.getItem(PREFILL_KEY)
      if (!raw) return null
      return JSON.parse(raw) as AmazonListing
    } catch {
      return null
    }
  },
  (_get, set, listing: AmazonListing | null) => {
    set(_rawPrefillAtom, listing)
    try {
      if (listing) {
        sessionStorage.setItem(PREFILL_KEY, JSON.stringify(listing))
      } else {
        sessionStorage.removeItem(PREFILL_KEY)
      }
    } catch { /* ignore */ }
  },
)

// ─── Derived atoms ───────────────────────────────────────────────────────────

/** Filtered listings based on search query and status filter */
export const filteredListingsAtom = atom((get) => {
  const listings = get(listingsAtom)
  const search = get(searchAtom).trim().toLowerCase()
  const statusFilter = get(statusFilterAtom)

  let result = listings

  // Apply status filter
  if (statusFilter !== 'all') {
    result = result.filter((l) => {
      const s = l.status.toLowerCase()
      if (statusFilter === 'active') return s === 'active'
      if (statusFilter === 'inactive') return s === 'inactive'
      // 'incomplete' = anything that's not active or inactive
      return s !== 'active' && s !== 'inactive'
    })
  }

  // Apply search filter
  if (search) {
    result = result.filter(
      (l) =>
        l.itemName.toLowerCase().includes(search) ||
        l.asin.toLowerCase().includes(search) ||
        l.sellerSku.toLowerCase().includes(search),
    )
  }

  return result
})

/** Status summary counts */
export const summaryAtom = atom((get) => {
  const listings = get(listingsAtom)
  const active = listings.filter((l) => l.status.toLowerCase() === 'active').length
  const inactive = listings.filter((l) => l.status.toLowerCase() === 'inactive').length
  const incomplete = listings.length - active - inactive
  return { active, inactive, incomplete }
})

// ─── Action atom ─────────────────────────────────────────────────────────────

/** Fetch listings from the backend SP-API endpoint */
export const fetchListingsAtom = atom(null, async (_get, set) => {
  set(loadingAtom, true)
  set(errorAtom, null)
  try {
    const res = await fetch(`${API_BASE}/amazon/listings`)
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error((data as any).error || `Request failed (${res.status})`)
    }
    const data = (await res.json()) as { listings: AmazonListing[]; count: number }
    set(listingsAtom, data.listings)   // writes to _rawListingsAtom + sessionStorage
    set(fetchedAtom, true)
  } catch (err) {
    set(errorAtom, err instanceof Error ? err.message : 'Failed to fetch listings')
  } finally {
    set(loadingAtom, false)
  }
})
